// @flow

import { PartialObserver, Subject } from 'rxjs'
import PaymentService from '../service/PaymentService/PaymentService';
import type { CreatePaymentRequest, UpdatePaymentRequest, Payment } from '../models/Payment/Payment';

export const PaymentEventTypesEnum = Object.freeze({
  CREATE_PAYMENT: Symbol("CREATE PAYMENT"),
  DELETE_PAYMENT: Symbol("DELETE PAYMENT"),
  FETCH_PAYMENTS_FOR_PROJECT: Symbol("FETCH PAYMENTS FOR PROJECT"),
  UPDATE_PAYMENT: Symbol("UPDATE PAYMENT"),
});

export type PaymentEventTypes = $Keys<typeof PaymentEventTypesEnum>;

export class PaymentEvent {
  type: PaymentEventTypes;

  constructor(type: PaymentEventTypes) {
    this.type = type;
  }
}

export class FetchPaymentsForProjectEvent extends PaymentEvent {
  projectId: string;

  constructor(projectId: string) {
    super(PaymentEventTypesEnum.FETCH_PAYMENTS_FOR_PROJECT);
    this.projectId = projectId;
  }
}

export class CreatePaymentForProjectEvent extends PaymentEvent {
  request: CreatePaymentRequest;

  constructor(createPaymentRequest: CreatePaymentRequest) {
    super(PaymentEventTypesEnum.CREATE_PAYMENT);
    this.request = createPaymentRequest;
  }
}

export class DeletePaymentForProjectEvent extends PaymentEvent {
  projectId: string;
  paymentId: string;

  constructor(projectId: string, paymentId: string) {
    super(PaymentEventTypesEnum.DELETE_PAYMENT);
    this.projectId = projectId;
    this.paymentId = paymentId;
  }
}

export class UpdatePaymentForProjectEvent extends PaymentEvent {
  request: UpdatePaymentRequest;

  constructor(updatePaymentRequest: UpdatePaymentRequest) {
    super(PaymentEventTypesEnum.UPDATE_PAYMENT);
    this.request = updatePaymentRequest;
  }
}

export class PaymentBloc {
  _outPaymentStream: Subject = new Subject();

  subscribeToPaymentContext(observer?: PartialObserver<PaymentOutEvent>) {
    return this._outPaymentStream.subscribe(observer);
  }

  _eventController: Subject = new Subject();

  sendEvent(event: PaymentEvent) {
    return this._eventController.next(event)
  }

  _paymentService: PaymentService;

  constructor(paymentService: PaymentService) {
    this._paymentService = paymentService;
    this._eventController.subscribe(this.buildEventHandler())
  }

  buildEventHandler = () => {
    return {
      next: async (event: PaymentEvent) => {
        try {
          switch (event.type) {
            case PaymentEventTypesEnum.FETCH_PAYMENTS_FOR_PROJECT: {
              const projectId = (event: FetchPaymentsForProjectEvent).projectId;
              try {
                const payments = await this._paymentService.getPaymentsForProject(projectId);
                this._outPaymentStream.next(new FetchPaymentsForProjectOutEvent(projectId, payments));
              } catch (error) {
                console.warn("Payment API not available yet. Showing empty payments list.");
                this._outPaymentStream.next(new FetchPaymentsForProjectOutEvent(projectId, []));
              }
              break;
            }

            case PaymentEventTypesEnum.CREATE_PAYMENT: {
              const projectId = (event: CreatePaymentForProjectEvent).request.projectId;
              const payment = await this._paymentService.createPayment((event: CreatePaymentForProjectEvent).request);
              this._outPaymentStream.next(new AddPaymentsForProjectOutEvent(projectId, [payment]));
              // Also fetch updated list to ensure consistency
              try {
                const payments = await this._paymentService.getPaymentsForProject(projectId);
                this._outPaymentStream.next(new FetchPaymentsForProjectOutEvent(projectId, payments));
              } catch (error) {
                console.warn("Failed to refresh payments list after creation:", error);
              }
              break;
            }

            case PaymentEventTypesEnum.DELETE_PAYMENT: {
              const { projectId, paymentId } = (event: DeletePaymentForProjectEvent);
              await this._paymentService.deletePayment(projectId, paymentId);
              const payments = await this._paymentService.getPaymentsForProject(projectId);
              this._outPaymentStream.next(new DeletePaymentsForProjectOutEvent(projectId, payments));
              break;
            }

            case PaymentEventTypesEnum.UPDATE_PAYMENT: {
              const projectId = (event: UpdatePaymentForProjectEvent).request.projectId;
              const payment = await this._paymentService.updatePayment((event: UpdatePaymentForProjectEvent).request);
              this._outPaymentStream.next(new UpdatePaymentsForProjectOutEvent(projectId, [payment]));
              // Also fetch updated list to ensure consistency
              try {
                const payments = await this._paymentService.getPaymentsForProject(projectId);
                this._outPaymentStream.next(new FetchPaymentsForProjectOutEvent(projectId, payments));
              } catch (error) {
                console.warn("Failed to refresh payments list after update:", error);
              }
              break;
            }

            default: {
              throw new Error("Unknown payment event: " + event.type);
            }
          }
        } catch (err) {
          this._outPaymentStream.error(err);
        }
      },
      error(err) {
        console.error("PaymentBloc error:", err);
      },
    };
  };

  dispose() {
    this._outPaymentStream.complete();
    this._eventController.complete();
  }
}

export const PaymentOutEventTypesEnum = Object.freeze({
  FETCHED: Symbol("FETCHED"),
  CREATED: Symbol("CREATED"),
  DELETED: Symbol("DELETED"),
  UPDATED: Symbol("UPDATED"),
});

export type PaymentOutEventTypes = $Keys<typeof PaymentOutEventTypesEnum>;

export class PaymentOutEvent {
  type: PaymentOutEventTypes;

  constructor(type: PaymentOutEventTypes) {
    this.type = type;
  }
}

export class FetchPaymentsForProjectOutEvent extends PaymentOutEvent {
  projectId: string;
  payments: Array<Payment>;

  constructor(projectId: string, payments: Array<Payment>) {
    super(PaymentOutEventTypesEnum.FETCHED);
    this.payments = payments;
    this.projectId = projectId;
  }
}

export class AddPaymentsForProjectOutEvent extends PaymentOutEvent {
  projectId: string;
  newPayments: Array<Payment>;

  constructor(projectId: string, newPayments: Array<Payment>) {
    super(PaymentOutEventTypesEnum.CREATED);
    this.newPayments = newPayments;
    this.projectId = projectId;
  }
}

export class DeletePaymentsForProjectOutEvent extends PaymentOutEvent {
  projectId: string;
  payments: Array<Payment>;

  constructor(projectId: string, payments: Array<Payment>) {
    super(PaymentOutEventTypesEnum.DELETED);
    this.payments = payments;
    this.projectId = projectId;
  }
}

export class UpdatePaymentsForProjectOutEvent extends PaymentOutEvent {
  projectId: string;
  newPayments: Array<Payment>;

  constructor(projectId: string, newPayments: Array<Payment>) {
    super(PaymentOutEventTypesEnum.UPDATED);
    this.newPayments = newPayments;
    this.projectId = projectId;
  }
}
